// src/components/setup/steps/DropshippingKYC.jsx
import React, { useState, useRef, useEffect } from 'react';
import { FileText, Upload, CheckCircle } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import { uploadKycDocuments, updateDropshippingSettings } from '../../../services/api/dropshipping';
import styles from './StepComponents.module.css';

const DropshippingKYC = ({ onNext, isSubmitting }) => {
  const { t } = useLocalization();
  const { success, error } = useAlert();

  const [kycFiles, setKycFiles] = useState({
    kycNicFront: null,
    kycNicBack: null,
    kycBusinessRegImage: null,
  });
  const [previews, setPreviews] = useState({
    kycNicFront: null,
    kycNicBack: null,
    kycBusinessRegImage: null,
  });
  const [uploading, setUploading] = useState(false);

  const nicFrontRef = useRef();
  const nicBackRef = useRef();
  const bizRegRef = useRef();

  // Revoke object URLs on unmount to avoid memory leaks
  useEffect(() => {
    return () => {
      Object.values(previews).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFileChange = (field, file) => {
    // Revoke previous preview URL
    if (previews[field]) URL.revokeObjectURL(previews[field]);
    const previewUrl = file ? URL.createObjectURL(file) : null;
    setKycFiles((prev) => ({ ...prev, [field]: file || null }));
    setPreviews((prev) => ({ ...prev, [field]: previewUrl }));
  };

  const fileFields = [
    { field: 'kycNicFront',         labelKey: 'dropshipping.nicFront',         ref: nicFrontRef, required: true  },
    { field: 'kycNicBack',          labelKey: 'dropshipping.nicBack',          ref: nicBackRef,  required: true  },
    { field: 'kycBusinessRegImage', labelKey: 'dropshipping.businessRegImage', ref: bizRegRef,   required: false },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!kycFiles.kycNicFront || !kycFiles.kycNicBack) {
      error(t('dropshipping.errorNicRequired'));
      return;
    }

    setUploading(true);
    try {
      // Enable reseller mode first
      await updateDropshippingSettings({ isResellerEnabled: true });

      // Upload KYC documents
      const fd = new FormData();
      if (kycFiles.kycNicFront)         fd.append('kycNicFront',         kycFiles.kycNicFront);
      if (kycFiles.kycNicBack)          fd.append('kycNicBack',          kycFiles.kycNicBack);
      if (kycFiles.kycBusinessRegImage) fd.append('kycBusinessRegImage', kycFiles.kycBusinessRegImage);
      await uploadKycDocuments(fd);

      success(t('setup.kycSubmittedSuccess'));
      onNext();
    } catch (err) {
      error(err.response?.data?.message || t('setup.kycSubmitError'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={styles.stepContainer}>
      <div className={styles.stepHeader}>
        <FileText className={styles.stepIcon} size={48} />
        <h2>{t('setup.dropshippingKycTitle')}</h2>
        <p>{t('setup.dropshippingKycDescription')}</p>
      </div>

      <form onSubmit={handleSubmit} className={styles.stepForm}>
        {fileFields.map(({ field, labelKey, ref, required }) => (
          <div key={field} className={styles.formGroup}>
            <label className={styles.fieldLabel}>
              {t(labelKey)}
              {required
                ? <span style={{ color: 'var(--color-danger)', marginLeft: '0.25rem' }}>*</span>
                : <span style={{ color: 'var(--color-text-muted)', marginLeft: '0.375rem', fontSize: '0.8em' }}>({t('common.optional') || 'optional'})</span>
              }
            </label>
            <div
              className={`${styles.kycDropzone} ${kycFiles[field] ? styles.kycDropzoneHasFile : ''}`}
              onClick={() => ref.current?.click()}
            >
              {previews[field] ? (
                <img
                  src={previews[field]}
                  alt={t(labelKey)}
                  style={{ maxHeight: '140px', maxWidth: '100%', objectFit: 'contain', borderRadius: '6px' }}
                />
              ) : (
                <>
                  <Upload size={20} />
                  <span>{t('dropshipping.clickToUpload')} {t('dropshipping.orDragDrop')}</span>
                  <small>{t('dropshipping.fileTypes')}</small>
                </>
              )}
              {previews[field] && (
                <span style={{ fontSize: '0.8em', color: 'var(--color-text-muted)', marginTop: '0.25rem' }}>
                  {kycFiles[field]?.name}
                </span>
              )}
            </div>
            <input
              ref={ref}
              type="file"
              accept="image/*,.pdf"
              style={{ display: 'none' }}
              onChange={(e) => handleFileChange(field, e.target.files[0] || null)}
            />
          </div>
        ))}

        <div className={styles.stepActions}>
          <button
            type="submit"
            disabled={uploading || isSubmitting || !kycFiles.kycNicFront || !kycFiles.kycNicBack}
            className={styles.primaryButton}
          >
            {uploading ? (
              <>
                <div className={styles.loadingSpinner}></div>
                {t('setup.uploadingKyc')}
              </>
            ) : (
              <>
                <CheckCircle size={20} />
                {t('setup.submitKyc')}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DropshippingKYC;
